import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/** Görev kuralları (cp_task_templates): olay olunca ya da elle çalıştırınca açılacak görevler. */
export const RULE_EVENTS = ['manual', 'demo.created', 'demo.expiring', 'demo.expired', 'demo.converted', 'order.approved', 'meeting.planned', 'sales.stage_changed', 'install.stage_changed'] as const
export type RuleEvent = (typeof RULE_EVENTS)[number]
export const MATCH_TYPES = ['always', 'group', 'each_module', 'module', 'deliverable', 'meeting_kind', 'stage'] as const
export type MatchType = (typeof MATCH_TYPES)[number]

/** Olay başına geçerli koşullar (sunucudaki ValidateTemplate ile aynı); ilki varsayılandır. */
export const MATCH_BY_EVENT: Record<RuleEvent, MatchType[]> = {
  manual: ['group'],
  'order.approved': ['always', 'each_module', 'module', 'deliverable'],
  'meeting.planned': ['always', 'meeting_kind'],
  'sales.stage_changed': ['always', 'stage'],
  'install.stage_changed': ['always', 'stage'],
  'demo.created': ['always'],
  'demo.expiring': ['always'],
  'demo.expired': ['always'],
  'demo.converted': ['always'],
}

export type TaskRule = {
  id: string
  event: RuleEvent
  matchType: MatchType
  matchValue: string
  title: string
  taskType: string
  description: string
  assigneeRole: string
  boardCode: string
  columnKey: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  dueInDays: number | null
  position: number
  isActive: boolean
}

export type RuleInput = Omit<TaskRule, 'id'>
export type Slot = { role: string; tasks: string[] }

const keys = { all: ['task-rules'] as const }
const s = (v: unknown) => (v == null ? '' : String(v))

function mapRule(r: Record<string, unknown>): TaskRule {
  const prio = s(r.priority)
  return {
    id: s(r.id),
    event: s(r.event) as RuleEvent,
    matchType: s(r.match_type) as MatchType,
    matchValue: s(r.match_value),
    title: s(r.title),
    taskType: s(r.task_type),
    description: s(r.description),
    assigneeRole: s(r.assignee_role),
    boardCode: s(r.board_code) || 'teknik',
    columnKey: s(r.column_key) || 'backlog',
    priority: prio === 'low' || prio === 'high' || prio === 'urgent' ? prio : 'normal',
    dueInDays: r.due_in_days == null ? null : Number(r.due_in_days),
    position: Number(r.position) || 0,
    isActive: r.is_active !== false,
  }
}

const body = (r: RuleInput) => ({
  event: r.event,
  match_type: r.matchType,
  match_value: r.matchType === 'always' || r.matchType === 'each_module' ? '' : r.matchValue,
  title: r.title,
  task_type: r.taskType,
  description: r.description,
  assignee_role: r.assigneeRole,
  board_code: r.boardCode,
  column_key: r.columnKey,
  priority: r.priority,
  due_in_days: r.dueInDays,
  position: r.position,
  is_active: r.isActive,
})

export const useTaskRules = () =>
  useQuery({ queryKey: keys.all, queryFn: async () => asList(await api.get<Record<string, unknown>[]>('/api/v1/task-templates')).map(mapRule) })

export const useRuleGroups = () =>
  useQuery({ queryKey: [...keys.all, 'groups'], queryFn: async () => asList(await api.get<string[]>('/api/v1/task-templates/groups')).map(String) })

/** Kural tetiklenmeden önce personel seçilmesi gereken roller. */
export const useRuleSlots = (q: { event: RuleEvent; group?: string; accountId?: string; projectId?: string }, enabled = true) =>
  useQuery({
    queryKey: [...keys.all, 'slots', q.event, q.group ?? '', q.accountId ?? '', q.projectId ?? ''],
    enabled: enabled && (q.event !== 'manual' || !!q.group),
    queryFn: async () =>
      asList(
        await api.get<Record<string, unknown>[]>('/api/v1/task-templates/slots', {
          event: q.event,
          ...(q.group ? { group: q.group } : {}),
          ...(q.accountId ? { account_id: q.accountId } : {}),
          ...(q.projectId ? { project_id: q.projectId } : {}),
        }),
      ).map((r): Slot => ({ role: s(r.role), tasks: Array.isArray(r.tasks) ? r.tasks.map(String) : [] })),
  })

export function useRuleMutations() {
  const qc = useQueryClient()
  const onSuccess = () => qc.invalidateQueries({ queryKey: keys.all })
  return {
    save: useMutation({ mutationFn: ({ id, input }: { id?: string; input: RuleInput }) => (id ? api.put(`/api/v1/task-templates/${id}`, body(input)) : api.post('/api/v1/task-templates', body(input))), onSuccess }),
    remove: useMutation({ mutationFn: (id: string) => api.del(`/api/v1/task-templates/${id}`), onSuccess }),
    run: useMutation({
      mutationFn: (input: { group: string; accountId?: string; projectId?: string; assignments: Record<string, string>; force?: boolean }) =>
        api.post<{ task_ids?: string[] }>('/api/v1/task-templates/run', {
          group: input.group,
          account_id: input.accountId || undefined,
          project_id: input.projectId || undefined,
          assignments: input.assignments,
          force: input.force || undefined,
        }),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
    }),
  }
}
